import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Search, ExternalLink } from "lucide-react";
import { Badge } from "@/components/Badge";
import { SCREENS, type Role } from "@/data/screens";
import { roleLabel } from "@/lib/roles";

const MODULES_ES = ["Todos", "Auth", "Tablero", "Gobierno", "Finanzas", "Residente", "Documentos", "Legal", "Seguridad", "Personal", "Admin", "Público"];
const ROLE_VARIANTS: Record<Role, "info" | "warning" | "success" | "default"> = {
  ADMIN: "warning",
  BOARD_MEMBER: "info",
  RESIDENT: "success",
  PUBLIC: "default",
};

export function HelpScreensPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const t = (es: string, en: string) => (isEn ? en : es);

  const [query, setQuery] = useState("");
  const [moduleFilter, setModuleFilter] = useState("Todos");

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return SCREENS.filter((s) => {
      if (moduleFilter !== "Todos" && s.module !== moduleFilter) return false;
      if (!q) return true;
      const hay = [s.path, s.titleEs, s.titleEn, s.purposeEs, s.purposeEn, s.module].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [query, moduleFilter]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/help" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
          <ArrowLeft size={14} /> {t("Centro de Ayuda", "Help Center")}
        </Link>
        <span className="text-sm text-slate-500">{filtered.length} / {SCREENS.length} {t("pantallas", "screens")}</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold">{t("Guía Pantalla por Pantalla", "Screen-by-Screen Guide")}</h1>
        <p className="text-slate-600 mt-1">
          {t("Referencia detallada de cada pantalla del sistema.", "Detailed reference for every screen in the system.")}
        </p>
      </div>

      <div className="card flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search size={16} className="text-slate-400" />
          <input
            className="input flex-1"
            placeholder={t("Buscar pantalla, acción, palabra…", "Search screen, action, keyword…")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select className="input max-w-[200px]" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
          {MODULES_ES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="space-y-4">
        {filtered.length === 0 ? (
          <div className="card text-slate-500 text-center">
            {t("No se encontraron pantallas con esos criterios.", "No screens matched those criteria.")}
          </div>
        ) : (
          filtered.map((s) => (
            <div key={s.path} className="card">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="info">{s.module}</Badge>
                    {s.roles.map((r) => (
                      <Badge key={r} variant={ROLE_VARIANTS[r]}>{roleLabel(r, isEn ? "en" : "es")}</Badge>
                    ))}
                    <code className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{s.path}</code>
                  </div>
                  <h2 className="text-lg font-semibold">{isEn ? s.titleEn : s.titleEs}</h2>
                </div>
                {!s.path.includes(":") && (
                  <Link to={s.path} className="btn-secondary text-xs shrink-0">
                    <ExternalLink size={12} /> {t("Abrir", "Open")}
                  </Link>
                )}
              </div>

              <p className="text-sm text-slate-700 mb-3">{isEn ? s.purposeEn : s.purposeEs}</p>

              <div>
                <h3 className="text-xs font-semibold uppercase text-slate-500 mb-1">
                  {t("Acciones disponibles", "Available actions")}
                </h3>
                <ul className="text-sm space-y-1">
                  {(isEn ? s.actionsEn : s.actionsEs).map((a, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary-500 mt-1">▸</span>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {((isEn ? s.tipsEn : s.tipsEs) ?? []).length > 0 && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                  <h3 className="text-xs font-semibold uppercase text-amber-700 mb-1">
                    {t("Consejos", "Tips")}
                  </h3>
                  <ul className="space-y-1">
                    {(isEn ? s.tipsEn : s.tipsEs)!.map((tip, i) => (
                      <li key={i} className="text-amber-900">{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
